/**
 * GTM dataLayer helper utilities.
 *
 * All functions are safe to call during SSR — they no-op when window is absent.
 * Import and call these from any client component or event handler.
 */

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function push(data: any): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * Track a virtual page view (useful with router.events or next/navigation).
 */
export function trackPageView(url: string): void {
  push({ event: "pageview", page: url });
}

/**
 * Track a generic custom event.
 */
export function trackEvent(
  category: string,
  action: string,
  label?: string | null,
  value?: string | number | null
): void {
  push({
    event: "custom_event",
    event_category: category,
    event_action: action,
    event_label: label ?? null,
    event_value: value ?? null,
  });
}

/**
 * Track enrollment intent (CTA click / enquiry form open on a course page).
 */
export function trackCourseEnroll(courseName: string, courseSlug: string): void {
  push({
    event: "course_enroll_intent",
    course_name: courseName,
    course_slug: courseSlug,
  });
}

/**
 * Track a form submission.
 */
export function trackFormSubmit(formName: string, page: string): void {
  push({
    event: "form_submit",
    form_name: formName,
    page,
  });
}

/**
 * Track a blog post read / engagement.
 */
export function trackBlogRead(blogTitle: string, category: string): void {
  push({
    event: "blog_read",
    blog_title: blogTitle,
    blog_category: category,
  });
}

/**
 * Track a phone number click.
 */
export function trackPhoneClick(number: string, page: string): void {
  push({
    event: "phone_click",
    phone_number: number,
    page,
  });
}

export const trackWhatsAppClick = (context: string, message?: string): void => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "whatsapp_click",
      whatsapp_context: context,
      whatsapp_message_type: message || "default",
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }
};

export const trackLeadFormOpen = (context: string): void => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "lead_form_open",
      form_context: context,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString(),
    });
  }
};

export const trackLeadFormSubmit = (context: string): void => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "lead_form_submit",
      form_context: context,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString(),
    });
  }
};

export interface TrackableCourse {
  slug?: string;
  title?: string;
  name?: string;
  category?: string;
  price?: number | string;
}

function normalizeCourseItem(course?: TrackableCourse) {
  const slug = String(course?.slug || "");

  return {
    item_id: slug.replace(/^\/courses\//, "") || slug || "unknown-course",
    item_name: String(course?.title || course?.name || "Course"),
    item_category: String(course?.category || "Courses"),
    price: Number(course?.price || 0),
  };
}

/**
 * GA4 ecommerce: view_item
 * Fire on course detail page view.
 */
export function trackViewItem(course?: TrackableCourse): void {
  push({
    event: "view_item",
    ecommerce: {
      currency: "USD",
      items: [normalizeCourseItem(course)],
    },
  });
}

/**
 * GA4 ecommerce: add_to_cart
 * Fire on enroll CTA click.
 */
export function trackAddToCart(course?: TrackableCourse): void {
  push({
    event: "add_to_cart",
    ecommerce: {
      currency: "USD",
      items: [normalizeCourseItem(course)],
    },
  });
}

/**
 * GA4 ecommerce: begin_checkout
 * Fire when enrollment/contact inquiry form is submitted.
 */
export function trackBeginCheckout(course?: TrackableCourse): void {
  push({
    event: "begin_checkout",
    ecommerce: {
      currency: "USD",
      items: [normalizeCourseItem(course)],
    },
  });
}

/**
 * GA4 lead event.
 */
export function trackGenerateLead(formData: Record<string, unknown> = {}): void {
  push({
    event: "generate_lead",
    value: 1,
    currency: "USD",
    ...formData,
  });
}

/**
 * GA4 site search event.
 */
export function trackSearch(searchTerm?: string): void {
  push({
    event: "search",
    search_term: searchTerm || "",
  });
}
